// ============================================================================
// PresenterPanel — JANELA DO APRESENTADOR (rota /apresentador).
// Abre em janela separada da apresentação pública e sincroniza via
// BroadcastChannel('sd2_presenter'):
//   - recebe 'state' {idx, total, slides, destaques} da janela pública;
//   - envia 'nav' (anterior/próximo) e 'goto';
//   - pede 'gerar' (a janela pública monta o payload e chama a API).
// O roteiro NUNCA aparece na janela compartilhada — só aqui.
// ============================================================================
import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  BookOpen, ChevronLeft, ChevronRight, Pencil, RefreshCw, Copy, Loader2,
  Crown, AlertTriangle, TrendingUp, MonitorPlay,
} from 'lucide-react';
import { getRoteiros, saveRoteiro } from '../api';

const SAMSUNG = "'Samsung Sharp Sans', 'SamsungOne', 'SamsungOne UI', sans-serif";
const MONO = `'JetBrains Mono', 'SF Mono', ui-monospace, Menlo, monospace`;
const TONE = {
  green:   { txt: '#008577', bg: 'rgba(0,195,178,0.10)',  border: 'rgba(0,195,178,0.25)' },
  amber:   { txt: '#B26A00', bg: 'rgba(255,181,70,0.10)', border: 'rgba(255,181,70,0.28)' },
  red:     { txt: '#B91C1C', bg: 'rgba(239,68,68,0.08)',  border: 'rgba(239,68,68,0.22)' },
  neutral: { txt: '#1428A0', bg: 'rgba(20,40,160,0.06)',  border: 'rgba(20,40,160,0.16)' },
};
const ICON_BY_TONE = { green: Crown, amber: AlertTriangle, red: AlertTriangle, neutral: TrendingUp };

export default function PresenterPanel() {
  const [st, setSt] = useState(null);            // {idx, total, slides, destaques}
  const [roteiros, setRoteiros] = useState({});
  const [generating, setGenerating] = useState([]);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [copied, setCopied] = useState(false);
  const [conectado, setConectado] = useState(false);
  const chRef = useRef(null);

  const refresh = useCallback(() => {
    getRoteiros().then(r => {
      setRoteiros(r.data.roteiros || {});
      setGenerating(r.data.generating || []);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    document.title = 'Painel do Apresentador — Sales Day';
    refresh();
    if (typeof BroadcastChannel === 'undefined') return;
    const ch = new BroadcastChannel('sd2_presenter');
    chRef.current = ch;
    const onMsg = (ev) => {
      const d = ev.data || {};
      if (d.type === 'state') { setSt({ idx: d.idx, total: d.total, slides: d.slides || [], destaques: d.destaques || [] }); setConectado(true); }
      else if (d.type === 'closed') { setConectado(false); }
      else if (d.type === 'gerado') { setBusy(false); setErr(''); refresh(); }
      else if (d.type === 'gerar-erro') { setBusy(false); setErr('Falha ao gerar: ' + (d.erro || '')); }
    };
    ch.addEventListener('message', onMsg);
    // Pede o estado até a apresentação responder — e PARA depois de conectar.
    let conectou = false;
    const onMsgConn = (ev) => { if (ev.data?.type === 'state') conectou = true; };
    ch.addEventListener('message', onMsgConn);
    ch.postMessage({ type: 'hello' });
    const t = setInterval(() => {
      if (!chRef.current || conectou) { clearInterval(t); return; }
      ch.postMessage({ type: 'hello' });
    }, 2000);
    return () => { clearInterval(t); ch.removeEventListener('message', onMsgConn); ch.removeEventListener('message', onMsg); ch.close(); chRef.current = null; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // Para o "hello" repetido quando conecta.
  useEffect(() => { if (conectado) { /* handshake ok */ } }, [conectado]);

  // Sai do modo edição ao trocar de slide (rascunho fica preservado por slide)
  // e REBUSCA os textos — se regeneraram no servidor, o painel pega a versão nova.
  const draftsRef = useRef({});
  const idx = st?.idx ?? 0;
  useEffect(() => { setEditing(false); setErr(''); setCopied(false); refresh(); }, [idx, refresh]);
  // Polling leve: enquanto o painel está aberto, atualiza os textos a cada 20s
  // (pega regenerações em segundo plano sem precisar recarregar a janela).
  useEffect(() => {
    const t = setInterval(refresh, 20000);
    return () => clearInterval(t);
  }, [refresh]);

  const slide = st?.slides?.[st.idx] || null;
  const next = st?.slides?.[st.idx + 1] || null;
  const rot = slide ? roteiros[slide.key] : null;
  const rotNext = next ? roteiros[next.key] : null;
  const gen = slide ? generating.includes(slide.key) : false;

  const nav = (delta) => chRef.current?.postMessage({ type: 'nav', delta });

  const copiar = async (texto) => {
    try { if (navigator.clipboard?.writeText) { await navigator.clipboard.writeText(texto); return true; } } catch { /* fallback */ }
    try {
      const ta = document.createElement('textarea');
      ta.value = texto; ta.style.position = 'fixed'; ta.style.opacity = '0';
      document.body.appendChild(ta); ta.select();
      const ok = document.execCommand('copy'); document.body.removeChild(ta); return ok;
    } catch { return false; }
  };

  // Teclado: ← → navegam a apresentação a partir do painel.
  useEffect(() => {
    const h = (e) => {
      const tag = e.target && e.target.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA') return;
      if (e.key === 'ArrowRight' || e.key === 'PageDown' || e.key === ' ') { e.preventDefault(); nav(1); }
      else if (e.key === 'ArrowLeft' || e.key === 'PageUp') { e.preventDefault(); nav(-1); }
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, []);

  const btn = (primary) => ({
    display: 'inline-flex', alignItems: 'center', gap: 6, padding: '9px 14px', borderRadius: 10,
    border: primary ? 'none' : '1px solid #e2e8f0', background: primary ? '#0f766e' : '#fff',
    color: primary ? '#fff' : '#334155', fontSize: 12.5, fontWeight: 800, cursor: 'pointer', fontFamily: SAMSUNG,
  });

  return (
    <div style={{ minHeight: '100vh', background: '#0f172a', color: '#e2e8f0', fontFamily: SAMSUNG, display: 'flex', flexDirection: 'column' }}>
      <style>{`@keyframes spin { from { transform: rotate(0deg) } to { transform: rotate(360deg) } }`}</style>

      {/* Topo */}
      <div style={{ padding: '14px 20px', borderBottom: '1px solid rgba(148,163,184,0.18)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 9, fontSize: 14, fontWeight: 800, color: '#5eead4' }}>
          <MonitorPlay size={17} /> Painel do Apresentador
        </span>
        <span style={{ fontSize: 11.5, fontWeight: 700, color: conectado ? '#34d399' : '#f59e0b' }}>
          {conectado ? '● sincronizado com a apresentação' : '○ aguardando a apresentação abrir…'}
        </span>
      </div>

      <p style={{ margin: 0, padding: '10px 20px', fontSize: 11.5, color: '#94a3b8', borderBottom: '1px solid rgba(148,163,184,0.12)' }}>
        Compartilhe somente a janela da apresentação. Este painel fica visível apenas para você.
      </p>

      {!slide ? (
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8', fontSize: 14, padding: 24, textAlign: 'center' }}>
          Abra o Sales Day 2.0 e clique em “Iniciar modo apresentador”.<br />Este painel conecta sozinho quando a apresentação abrir.
        </div>
      ) : (
        <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px 90px' }}>
          {/* Slide atual */}
          <p style={{ fontSize: 11, fontWeight: 800, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.14em', margin: 0 }}>
            Slide {st.idx + 1} de {st.total}
          </p>
          <h1 style={{ fontSize: 24, fontWeight: 800, color: '#f8fafc', margin: '4px 0 2px' }}>{slide.titulo}</h1>
          {slide.subtitulo && <p style={{ fontSize: 12.5, color: '#94a3b8', margin: 0 }}>{slide.subtitulo}</p>}

          {/* Destaques do slide (mesmos cards da apresentação) */}
          {st.destaques?.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, margin: '14px 0 4px' }}>
              {st.destaques.map((d, i) => {
                const t = TONE[d.tone] || TONE.neutral;
                const Ic = ICON_BY_TONE[d.tone] || TrendingUp;
                return (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px', borderRadius: 10, background: t.bg, border: `1px solid ${t.border}` }}>
                    <Ic size={15} color={t.txt} style={{ flexShrink: 0 }} />
                    <span style={{ fontSize: 12, fontWeight: 800, color: t.txt, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{d.kicker}</span>
                    <span style={{ flex: 1 }} />
                    <span style={{ fontSize: 13, fontWeight: 800, color: '#f1f5f9' }}>{d.loja}</span>
                    <span style={{ fontSize: 14, fontWeight: 800, color: t.txt, fontFamily: MONO }}>{d.valor}</span>
                  </div>
                );
              })}
            </div>
          )}

          {/* O QUE FALAR */}
          <p style={{ fontSize: 11, fontWeight: 800, color: '#5eead4', textTransform: 'uppercase', letterSpacing: '0.14em', margin: '18px 0 8px' }}>O que falar</p>
          {slide.isCustom ? (
            <p style={{ fontSize: 13.5, color: '#94a3b8' }}>Slide personalizado — sem roteiro automático.</p>
          ) : editing ? (
            <textarea
              value={draft}
              onChange={(e) => { setDraft(e.target.value); draftsRef.current[slide.key] = e.target.value; }}
              autoFocus
              style={{ width: '100%', minHeight: 190, boxSizing: 'border-box', padding: '12px 14px', borderRadius: 12, border: '1px solid #334155', background: '#1e293b', fontSize: 15, lineHeight: 1.65, color: '#f1f5f9', fontFamily: 'inherit', resize: 'vertical', outline: 'none' }}
            />
          ) : gen && !rot?.texto ? (
            <p style={{ fontSize: 13.5, color: '#94a3b8', display: 'inline-flex', alignItems: 'center', gap: 8 }}>
              <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> Gerando roteiro…
            </p>
          ) : rot?.texto ? (
            <>
              {gen && <p style={{ fontSize: 11.5, color: '#5eead4', display: 'inline-flex', alignItems: 'center', gap: 6, margin: '0 0 8px' }}><Loader2 size={12} style={{ animation: 'spin 1s linear infinite' }} /> Atualizando com os dados novos…</p>}
              <p style={{ fontSize: 16.5, color: '#f1f5f9', lineHeight: 1.7, whiteSpace: 'pre-wrap', margin: 0 }}>{rot.texto}</p>
              <p style={{ fontSize: 10.5, color: '#64748b', marginTop: 10 }}>
                {rot.edited ? 'Editado manualmente' : 'Gerado pela IA'}
              </p>
            </>
          ) : (
            <p style={{ fontSize: 13.5, color: '#94a3b8' }}>Roteiro ainda não gerado para este slide.</p>
          )}
          {err && <p style={{ fontSize: 12, color: '#f87171', marginTop: 8 }}>{err}</p>}

          {/* Ações */}
          {!slide.isCustom && (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 14 }}>
              {!editing && <button style={btn(false)} onClick={() => { setDraft(draftsRef.current[slide.key] ?? (rot?.texto || '')); setEditing(true); setErr(''); }}><Pencil size={13} /> Editar{draftsRef.current[slide.key] != null ? ' (rascunho)' : ''}</button>}
              {editing && (<>
                <button style={btn(true)} disabled={busy} onClick={async () => { const k = slide.key; setBusy(true); setErr(''); try { await saveRoteiro(k, draft); delete draftsRef.current[k]; setRoteiros(prev => ({ ...prev, [k]: { ...(prev[k] || {}), texto: draft, edited: true } })); setEditing(false); } catch (e) { setErr('Falha ao salvar: ' + (e.response?.data?.detail || e.message)); } setBusy(false); }}>{busy ? 'Salvando…' : 'Salvar'}</button>
                <button style={btn(false)} onClick={() => { delete draftsRef.current[slide.key]; setEditing(false); setErr(''); }}>Cancelar</button>
              </>)}
              {!editing && (
                <button style={{ ...btn(false), opacity: (busy || gen) ? 0.6 : 1 }} disabled={busy || gen}
                  onClick={() => { setBusy(true); setErr(''); chRef.current?.postMessage({ type: 'gerar', key: slide.key }); }}>
                  <RefreshCw size={13} style={(busy || gen) ? { animation: 'spin 1s linear infinite' } : {}} /> {busy ? 'Gerando…' : gen ? 'Gerando em 2º plano…' : 'Gerar novamente'}
                </button>
              )}
              {!editing && rot?.texto && (
                <button style={btn(false)} onClick={async () => { const ok = await copiar(rot.texto); if (ok) { setCopied(true); setTimeout(() => setCopied(false), 1500); } }}>
                  <Copy size={13} /> {copied ? 'Copiado!' : 'Copiar'}
                </button>
              )}
            </div>
          )}

          {/* Próximo slide (prévia) */}
          {next && (
            <div style={{ marginTop: 22, padding: '12px 14px', borderRadius: 12, background: 'rgba(148,163,184,0.07)', border: '1px solid rgba(148,163,184,0.16)' }}>
              <p style={{ fontSize: 10.5, fontWeight: 800, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.12em', margin: 0 }}>A seguir · {st.idx + 2} de {st.total}</p>
              <p style={{ fontSize: 15, fontWeight: 800, color: '#cbd5e1', margin: '4px 0 3px' }}>{next.titulo}</p>
              {rotNext?.texto && <p style={{ fontSize: 12, color: '#94a3b8', lineHeight: 1.5, margin: 0, display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{rotNext.texto}</p>}
            </div>
          )}
        </div>
      )}

      {/* Navegação fixa */}
      <div style={{ position: 'fixed', left: 0, right: 0, bottom: 0, padding: '12px 20px', background: 'rgba(15,23,42,0.96)', borderTop: '1px solid rgba(148,163,184,0.18)', display: 'flex', gap: 10 }}>
        <button onClick={() => nav(-1)} disabled={!st || st.idx <= 0}
          style={{ flex: 1, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '13px 0', borderRadius: 12, border: '1px solid rgba(148,163,184,0.25)', background: 'rgba(148,163,184,0.08)', color: (!st || st.idx <= 0) ? '#475569' : '#e2e8f0', fontSize: 14, fontWeight: 800, cursor: 'pointer', fontFamily: SAMSUNG }}>
          <ChevronLeft size={18} /> Anterior
        </button>
        <button onClick={() => nav(1)} disabled={!st || st.idx >= st.total - 1}
          style={{ flex: 1, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '13px 0', borderRadius: 12, border: 'none', background: (!st || st.idx >= st.total - 1) ? 'rgba(148,163,184,0.15)' : '#0f766e', color: '#fff', fontSize: 14, fontWeight: 800, cursor: 'pointer', fontFamily: SAMSUNG }}>
          Próximo <ChevronRight size={18} />
        </button>
      </div>
      {/* Ícone só para manter o import usado */}
      <span style={{ display: 'none' }}><BookOpen size={1} /></span>
    </div>
  );
}
